import type { ParseKeys } from 'i18next';
import type { components } from '@/generated/api/schema';

/**
 * The four organization kinds in the DELABO network (QK-SW-18/ADR-SW-017), taken straight from the
 * generated wire union rather than re-typed by hand.
 *
 * Lives here rather than in `models/role.ts` (its previous home): an organization type is not a
 * role concept, and keeping it next to the role vocabulary is what let a second organization-type
 * label vocabulary grow there unnoticed. `models/role.ts` imports it from here.
 */
export type OrganizationType = components['schemas']['OrganizationType'];

/**
 * Customer organization kind offered by both self-registration (US-E10-03) and proxy customer
 * creation (US-E07-03) — the customer-facing subset of the backend's full organization-type
 * vocabulary (QK-SW-18/ADR-SW-017). DELABO-internal kinds (`DELABO_LAB`, `DELABO_PORTAL`) are
 * never customer records and are therefore excluded here — see {@link OrganizationType} above
 * for the full vocabulary.
 *
 * The contract now carries this restriction itself: `CreateCustomerRequest.type` and the
 * customer-list `type` filters reference the generated `CustomerOrganizationType` schema rather
 * than the full four-value `OrganizationType`. This tuple is pinned to that union below, so the
 * two can no longer drift apart silently.
 *
 * The single, feature-agnostic source of this constant (Finding 6) — `features/customers` and
 * `features/auth` both import it instead of maintaining their own copy.
 */
export const CUSTOMER_ORGANIZATION_TYPES = ['PRACTICE', 'CUSTOMER_LAB'] as const;

/** One of the two organization kinds a customer (self-registered or proxy-created) may be. */
export type CustomerOrganizationType = (typeof CUSTOMER_ORGANIZATION_TYPES)[number];

/**
 * Compile-time pin between this hand-written tuple and the generated `CustomerOrganizationType`
 * schema, in both directions — neither may carry a value the other lacks. Without it the tuple
 * could quietly fall behind a contract change (a value added to the schema would never reach the
 * filter bar) or run ahead of one (a value offered in the UI that the server rejects at binding).
 */
type CustomerTypeNotInContract = Exclude<
  CustomerOrganizationType,
  components['schemas']['CustomerOrganizationType']
>;
type ContractTypeNotInTuple = Exclude<
  components['schemas']['CustomerOrganizationType'],
  CustomerOrganizationType
>;
const _customerOrganizationTypesMatchTheContract: CustomerTypeNotInContract extends never
  ? ContractTypeNotInTuple extends never
    ? true
    : never
  : never = true;
void _customerOrganizationTypesMatchTheContract;

/**
 * The two DELABO-internal organization kinds (QK-SW-18/ADR-SW-017) — the complement of
 * {@link CUSTOMER_ORGANIZATION_TYPES} within the full vocabulary. Counterpart of the backend's
 * `OrganizationType.isDelaboIntern()`; keep both in step.
 */
export const DELABO_INTERNAL_ORGANIZATION_TYPES = ['DELABO_LAB', 'DELABO_PORTAL'] as const;

/**
 * The organization types a DELABO lab can see at all — its own (`DELABO_LAB`) plus the customer
 * types its assigned customers can have. The client-side mirror of the backend's
 * `StammdatenVisibility.benutzerverwaltungForActor` `DELABO_LAB` branch, which resolves the acting
 * lab's own organization OR a `customerTypeCondition()` organization whose `partner_lab_id` is
 * that lab.
 *
 * Exists because a DELABO lab's visible scope is **wider than its own organization type**, which
 * is the one place where "which roles may this audience filter by" cannot be answered from the
 * caller's own type alone: the Benutzerliste shows a lab its customers' users, so its role filter
 * has to offer the customer roles too (see `RoleFilterSelect`'s `organizationTypes`).
 *
 * Derived from {@link CUSTOMER_ORGANIZATION_TYPES} rather than spelled out, so widening the
 * customer vocabulary widens this scope with it — exactly as `customerTypeCondition()` does
 * server-side. Order is irrelevant to consumers: `roleGroupsForOrganizationTypes` re-imposes the
 * canonical group order.
 */
export const DELABO_LAB_VISIBLE_ORGANIZATION_TYPES: readonly OrganizationType[] = [
  'DELABO_LAB',
  ...CUSTOMER_ORGANIZATION_TYPES,
];

/**
 * Compile-time exhaustiveness guard: {@link CUSTOMER_ORGANIZATION_TYPES} and
 * {@link DELABO_INTERNAL_ORGANIZATION_TYPES} must together cover the full generated vocabulary.
 *
 * Without this, adding a fifth organization type to the OpenAPI contract would silently fall
 * through `isDelaboInternalOrganizationType` as "not internal" — and if that new type were in fact
 * DELABO-internal, the Mitarbeiter-CreatePage and the profile page would keep offering the
 * marketing-consent checkbox for it and reopen the `422 MARKETING_CONSENT_NOT_ALLOWED` path
 * through the UI. Failing to compile here forces whoever widens the contract to classify the new
 * value deliberately.
 */
type UnclassifiedOrganizationType = Exclude<
  components['schemas']['OrganizationType'],
  (typeof CUSTOMER_ORGANIZATION_TYPES)[number] | (typeof DELABO_INTERNAL_ORGANIZATION_TYPES)[number]
>;
const _everyOrganizationTypeIsClassified: UnclassifiedOrganizationType extends never ? true : never =
  true;
void _everyOrganizationTypeIsClassified;

/**
 * True for the two DELABO-internal organization kinds. Used to suppress the marketing-consent
 * checkbox on the Mitarbeiter-CreatePage: DELABO's own staff are never marketing recipients, and
 * the server rejects `marketingConsent=true` for these types with
 * `422 MARKETING_CONSENT_NOT_ALLOWED`.
 *
 * Typed against {@link OrganizationType} — itself the generated wire union — so call sites
 * holding an `OrganizationSearchResult['type']` need no cast.
 */
export function isDelaboInternalOrganizationType(type: OrganizationType): boolean {
  return DELABO_INTERNAL_ORGANIZATION_TYPES.some((internal) => internal === type);
}

/**
 * The i18n message keys {@link ORGANIZATION_TYPE_DISPLAY} maps an {@link OrganizationType} to,
 * scoped to the shared `organizationType` i18n namespace
 * (`shared/i18n/organizationType/de.json`) — consumers bind it with
 * `useTranslation('organizationType')`. Mirrors `RoleDisplay`'s scoping to `role`.
 *
 * <b>Two distinct facets, deliberately not one.</b> The same organization type is named
 * differently depending on what is being named:
 *
 * <ul>
 *   <li>{@link labelKey} names the **organization** — "Praxis", "Kundenlabor", "DELABO-Portal".
 *       This is what belongs next to an organization in a list, a picker, or a filter group
 *       heading: the row already says "Zahnarztpraxis Müller", so its type reads as "Praxis".</li>
 *   <li>{@link personaKey} names the **person behind it** — "Zahnarzt", "Dentallabor", "Support".
 *       This is what belongs under the signed-in user's own name in the user menu, where the
 *       subtitle describes who you are, not which legal entity you belong to.</li>
 * </ul>
 *
 * Merging them would force one of the two contexts to read wrong, which is exactly what happened
 * while the persona wording was the only one available: organization pickers labelled
 * "Zahnarztpraxis Müller" as "Zahnarzt". Keeping both here, in one record, is what makes the
 * distinction visible at the point where someone would otherwise reach for the wrong one.
 */
export interface OrganizationTypeDisplay {
  /** Key for the organization's own name, e.g. "Praxis". */
  readonly labelKey: ParseKeys<'organizationType'>;
  /** Key for the persona behind the organization type, e.g. "Zahnarzt". */
  readonly personaKey: ParseKeys<'organizationType'>;
}

/**
 * Maps each organization type to its i18n message keys in the shared `organizationType`
 * namespace. Only the **keys** live here, never the German wording — that stays single-sourced in
 * the i18n resource file, exactly as `ROLE_DISPLAY` (`models/role.ts`) and
 * `ORGANIZATION_STATE_DISPLAY` (`models/organizationState.ts`) handle their vocabularies.
 *
 * Typed as `Record<OrganizationType, …>` against the generated union, so a fifth organization
 * type added to the OpenAPI contract fails the build here until it has been given both a name and
 * a persona.
 */
const ORGANIZATION_TYPE_DISPLAY: Readonly<Record<OrganizationType, OrganizationTypeDisplay>> = {
  PRACTICE: { labelKey: 'practice', personaKey: 'persona_practice' },
  CUSTOMER_LAB: { labelKey: 'customer_lab', personaKey: 'persona_customer_lab' },
  DELABO_LAB: { labelKey: 'delabo_lab', personaKey: 'persona_delabo_lab' },
  DELABO_PORTAL: { labelKey: 'delabo_portal', personaKey: 'persona_delabo_portal' },
};

/**
 * Looks up the i18n keys for an organization type. Returns `null` for any value outside the closed
 * vocabulary — a type rolled out on the backend before this build knows about it, or a malformed
 * response — so callers render a fail-closed fallback (the raw value, or nothing) instead of a
 * bare `organizationType:…` key leaking into the UI. Mirrors `roleDisplay` and
 * `organizationStateDisplay`.
 */
export function organizationTypeDisplay(
  orgType: string | null | undefined,
): OrganizationTypeDisplay | null {
  return typeof orgType === 'string'
    ? (ORGANIZATION_TYPE_DISPLAY[orgType as OrganizationType] ?? null)
    : null;
}

/**
 * The persona key for the signed-in user's own organization type, for the user-menu subtitle.
 *
 * Unlike {@link organizationTypeDisplay} this is deliberately **not** fail-closed: an unknown or
 * absent organization type resolves to the `DELABO_PORTAL` persona ("Support") rather than to
 * `null`. That is the long-standing behaviour of the user menu and is intentional — the subtitle
 * sits under the user's name and would otherwise collapse to an empty line, and a principal whose
 * organization type this build cannot classify is, in practice, portal-internal. It is a display
 * fallback only and grants nothing: every actual permission decision goes through the role guards,
 * never through this label.
 */
export function organizationPersonaKey(
  orgType: string | null | undefined,
): ParseKeys<'organizationType'> {
  return organizationTypeDisplay(orgType)?.personaKey ?? 'persona_delabo_portal';
}
